import {
  Datagrid,
  TextField,
  useRecordContext,
  DateField,
  useTranslate,
  Pagination,
  Button,
  useRedirect,
} from 'react-admin';
import { ListLive, ListLiveProps } from '@react-admin/ra-realtime';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  TextFieldProps, Tooltip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PersonIcon from '@mui/icons-material/Person';
import { Alarm } from '@x-guard/xgac-types/xgac';
import TextFieldNullable from '../../../components/fields/TextFieldNullable';
import { httpClient } from '../../../utils/httpClient';
import { XGAC_MAIN_API_URL } from '../../../config';
import { RedirectToCreateAlarm } from '../components/redirectToCreateAlarm';
import { StyledButton } from '../buttons/StyledButton';
import { PageContentCard } from '../cards/PageContentCard';

const apiUrl = XGAC_MAIN_API_URL;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AssetFieldClickable = (props: TextFieldProps) => {

  const record = useRecordContext();

  if (!record?.asset) return <TextFieldNullable/>;
  return <TextField source="asset.name" />;

};

type AlarmHelper = {
  name: string;
  confirmed: boolean | null;
};

const getHelperResponseTranslation = (confirmed: AlarmHelper['confirmed']) => {

  if (confirmed === null) return 'resources.alarms.text.has_not_responded';
  return confirmed ? 'resources.alarms.text.will_help' : 'resources.alarms.text.will_not_help';

};

const getHelperResponseColor = (confirmed: AlarmHelper['confirmed']) => {

  if (confirmed === null) return '#888';
  return confirmed ? '#0F8F49' : '#f15a24';

};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HelperListField = (props: any) => {

  const record = useRecordContext<Alarm & { id: string }>();
  const redirect = useRedirect();
  const translate = useTranslate();

  const { data: helpers = [], isPending, isError } = useQuery<AlarmHelper[]>({
    queryKey: ['alarms', 'alarm-helpers', record?.id ?? ''],
    queryFn: async () => {

      if (!record?.id) return [];

      const { json } = await httpClient(`${apiUrl}/alarms/${record.id}/alarm-helpers?$max=100`);
      return json.result.map((helper: any) => ({
        name: helper.asset?.name || translate('resources.assets.text.bhvk_deleted'),
        confirmed: helper.confirmedHelping,
      }));

    },
    enabled: Boolean(record?.id),
  });

  if (!record?.id) return <TextFieldNullable/>;

  return (
    <div key={record.id}>
      {isError && (
        <Box component='div' sx={{ color: 'error.main' }}>
          {translate('resources.alarms.text.failed_to_load_notified_users')}
        </Box>
      )}
      {isPending && <CircularProgress size={20}/>}
      {!isPending && !isError && (helpers.length > 0 || record.alarmCenter?.name) && (
        <div>
          {helpers.map((helper: AlarmHelper) => (
            <Tooltip
              key={helper.name}
              title={translate(getHelperResponseTranslation(helper.confirmed))}
              placement="top"
              arrow>
              <div style={{ width: 'fit-content', color: getHelperResponseColor(helper.confirmed) }}>
                <PersonIcon
                  style={{ verticalAlign: 'middle', position: 'relative', top: '-2px' }}
                /> {helper.name}
              </div>
            </Tooltip>))}
        </div>
      )}
      {record.alarmCenter?.name && (
        <Tooltip title={translate('resources.alarms.fields.alarmCenter.name')} placement="top" arrow>
          <div style={{ width: 'fit-content', color: '#0F8F49' }}>
            {record.alarmCenter?.name}
          </div>
        </Tooltip>
      )}
      {!isPending && !isError && helpers.length === 0 && !record.alarmCenter?.name && (
        <Button
          label="resources.alarms.text.nobody_notified"
          onClick={() => redirect('/help/dashboard/waarom-wordt-er-niemand-opgeroepen')}
          size="small"
          variant='text'
          color='warning'
          style={{ marginLeft: -16 }}
        />
      )}
    </div>
  );

};

export const AlarmListSmall = (props: {
  empty?: ListLiveProps['empty'];
  filter?: ListLiveProps['filter'];
  actions?: ListLiveProps['actions'];
  hasPagination?: boolean;
}) => {

  const queryClient = useQueryClient();

  const handleAlarmEventReceived = useCallback(() => {

    queryClient.invalidateQueries({ queryKey: ['alarms'] });

  }, [queryClient]);

  return (
    <>
      <ListLive
        resource={'alarms'}
        onEventReceived={handleAlarmEventReceived}
        pagination={props.hasPagination ? <Pagination/> : false}
        perPage={50}
        actions={props.actions ? props.actions : false}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={props.empty ? props.empty : <RedirectToCreateAlarm/>}
        filter={props.filter}
        emptyWhileLoading
        sx={{ '& > .RaList-main > .RaList-content': { mt: '12px' } }}
        queryOptions={{
          meta: {
            $select: 'createdAt,customer,ack,severity,name,alarmCenter,asset',
          },
        }}
      >
        <Datagrid bulkActionButtons={false}>
          <DateField source="createdAt" showTime={true} label={'general.fields.createdAt'} />
          <TextField source="name" label={'general.fields.name'} sortable={false} />
          <AssetFieldClickable label={'general.fields.madeBy'}/>
          <HelperListField label="resources.alarms.text.notified"/>
        </Datagrid>
      </ListLive>
    </>
  );

};
export const AlarmList = () => {

  const redirect = useRedirect();
  const translate = useTranslate();

  const AlarmCreateButton = () => {

    return (
      <StyledButton onClick={() => {

        redirect('/maak-alarm');

      }} label={translate('menu.items.create_alarm')}/>
    );

  };

  return (
    <PageContentCard>
      <h3>{translate('menu.items.alarms')}</h3>
      <p>
        {translate('resources.alarms.text.title_subtext')}
      </p>
      <AlarmListSmall hasPagination={true} actions={<AlarmCreateButton/>} />
    </PageContentCard>
  );

};
